/* eslint-disable no-param-reassign */
import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'

// 游戏中心主页面信息 http://wiki.xunlei.cn/pages/viewpage.action?pageId=113908868
export const gameCenterHomePage = createAsyncThunk(
  'game_center_homepage',
  async (p: any, store: any) => {
    const resp = await http.post('/live/activity/game_center_homepage', {
      ...store.getState().env.deviceinfo,
      ...p,
    })
    return resp.data
  }
)

// 游戏等级奖励领取 http://wiki.xunlei.cn/pages/viewpage.action?pageId=113909093
export const gameCenterReceive = createAsyncThunk(
  'game_center_receive',
  async (p: any, store: any) => {
    const resp = await http.post('/live/activity/game_center_receive', {
      ...store.getState().env.deviceinfo,
      ...p,
    })
    return resp.data
  }
)

// 等级奖励列表 http://wiki.xunlei.cn/pages/viewpage.action?pageId=113909108
export const gameCenterGradeList = createAsyncThunk(
  'game_center_grade_list',
  async (p: any, store: any) => {
    const resp = await http.post('/live/activity/game_center_grade_list', {
      ...store.getState().env.deviceinfo,
      ...p,
      limit: 20,
    })
    return resp.data
  }
)

// 签到表 http://wiki.xunlei.cn/pages/viewpage.action?pageId=113909129
export const gameCenterSignInList = createAsyncThunk(
  'game_center_sign_in_list',
  async (p: any, store: any) => {
    const resp = await http.post('/live/activity/game_center_sign_in_list', {
      ...store.getState().env.deviceinfo,
      ...p,
      limit: 1000,
    })
    return resp.data
  }
)

export const enum TASKSTATUS {
  未完成 = 0,
  完成 = 1,
}
export const enum GRADESTATUS {
  未领取 = 1,
  已领取 = 2,
  未达到 = 3,
}
export const enum SIGNSTATUS {
  已签到 = 1,
  未签到 = 2,
  未到日期 = 3,
}

export interface MemberProps {
  avatar: string
  id: number
  name: string
  zy_id: string
}
export interface TaskProps {
  desc: string
  link: string
  target: number
  current: number
  status: TASKSTATUS
}
export interface GradeProps {
  grade: number
  reward_url: string
  target: number
  status: GRADESTATUS
  day: number
}
export interface ActivityProps {
  activity_url: string
  activity_link: string
}
export interface GameProps {
  tab_name: string
  game_url: string
  game_link: string
  activitys: ActivityProps[]
}
export interface SignInProps {
  date: string
  status: SIGNSTATUS
}
export interface TimeTaskProps {
  target_value: number
  reward_link: string
  reward_id: number
}

const initialState: {
  home_page: {
    current_period: string // 当前赛季时间
    member_info?: MemberProps // 个人信息
    current_grade: number // 当前等级
    grade_exps: number[] // 当前所处等级经验值范围
    current_exp: number // 当前经验值
    tasks: TaskProps[] // 任务列表
    grade_info: GradeProps[] // 等级奖励列表
    games: GameProps[] // 游戏列表
    has_upgrade_reward: boolean // 是否有升级奖励可以领取
    broad_cast_list?: {
      grade: number
      mid: number
      user_name: string
    }[]
    cut_down: number // 限时任务倒计时
    limit_time_mission?: {
      desc: string // 限时任务描述
      link: string // 限时任务跳转链接
      current: number // 限时任务当前分数
      list: TimeTaskProps[] // 限时任务列表
    }
  }
  grade_info_list: {
    grade_info: GradeProps[] // 等级奖励列表
    more: boolean
    offset: number
  }
  sign_in_list: {
    st: string // 下次请求的开始时间
    more: boolean
    time: string // 显示时间串
    total: number // 季度总签到天数
    sign_in_list: SignInProps[]
    msg?: string
  }
  rewards: {
    name: string
    icon: string
  }[]
} = {
  home_page: {
    current_period: '0',
    current_grade: 0,
    grade_exps: [],
    current_exp: 0,
    tasks: [],
    grade_info: [],
    games: [],
    has_upgrade_reward: false,
    cut_down: 0,
  },
  grade_info_list: {
    grade_info: [],
    more: false,
    offset: 0,
  },
  sign_in_list: {
    st: '',
    more: false,
    time: '0',
    total: 0,
    sign_in_list: [],
  },
  rewards: [],
}

export const GameCenterSlice = createSlice({
  name: 'GameCenter',
  initialState,
  reducers: {
    setRecive: (state, action: PayloadAction<number>) => {
      const grade = action.payload
      const homeGrade = state.home_page?.grade_info
      const homeIndex = homeGrade?.findIndex((item) => item?.grade === grade)
      if (homeIndex >= 0) {
        homeGrade[homeIndex].status = GRADESTATUS.已领取
        Object.assign(state.home_page, {grade_info: homeGrade})
      }
      const gradeList = state?.grade_info_list?.grade_info
      const gradeIndex = gradeList?.findIndex((item) => item?.grade === grade)
      if (gradeIndex >= 0) {
        gradeList[gradeIndex].status = GRADESTATUS.已领取
        Object.assign(state.grade_info_list, {grade_info: gradeList})
      }
    },
    setReciveAll: (state, action: PayloadAction<number>) => {
      const homeGrade = state.home_page?.grade_info
      Object.assign(state.home_page, {
        grade_info: homeGrade?.map((item, index) => {
          if (item?.status === GRADESTATUS.未领取) {
            return {...item, status: GRADESTATUS.已领取}
          }
          return item
        }),
      })
      const gradeList = state?.grade_info_list?.grade_info
      Object.assign(state.grade_info_list, {
        grade_info: gradeList?.map((item, index) => {
          if (item?.status === GRADESTATUS.未领取) {
            return {...item, status: GRADESTATUS.已领取}
          }
          return item
        }),
      })
    },
  },
  extraReducers: (builder) => {
    builder.addCase(gameCenterHomePage.fulfilled, (state, action) => {
      const {data = {}} = action.payload
      const {grade_info, tasks, ...ret} = data
      Object.assign(state.home_page, {
        ...ret,
        tasks: tasks || state.home_page.tasks,
        grade_info: grade_info || state.home_page.grade_info,
      })
    })
    builder.addCase(gameCenterGradeList.fulfilled, (state, action) => {
      const {data = {}} = action.payload
      if (data?.offset > 20) {
        Object.assign(state.grade_info_list, {
          ...data,
          grade_info: [
            ...state.grade_info_list?.grade_info,
            ...data?.grade_info,
          ],
        })
      } else {
        Object.assign(state.grade_info_list, data)
      }
    })
    builder.addCase(gameCenterSignInList.fulfilled, (state, action) => {
      const {data = {}} = action.payload
      const {sign_in_list} = state
      if (sign_in_list?.st && sign_in_list?.st !== data?.st) {
        Object.assign(state?.sign_in_list, {
          ...data,
          sign_in_list: [...sign_in_list.sign_in_list, ...data?.sign_in_list],
        })
      } else {
        Object.assign(state?.sign_in_list, data)
      }
    })
    builder.addCase(gameCenterReceive.fulfilled, (state, action) => {
      const {data = {}} = action.payload
      Object.assign(state, data)
    })
  },
})

export default GameCenterSlice.reducer
export const GameCenterSliceActions = GameCenterSlice.actions
